import { useMemo, useRef, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  UploadCloud, FileText, CheckCircle2, AlertTriangle, RefreshCw,
  Download, ArrowRight, ArrowLeft, Users, Home, Car, PawPrint, HardHat,
  KeyRound, ParkingSquare, Package,
} from "lucide-react";

// -----------------------------------------------------------------------------
// Bare-minimum CSV → array-of-objects parser (quoted fields, blank-line safe).
// Falls back to a friendly error instead of throwing so the caller can toast.
// -----------------------------------------------------------------------------
function parseCSV(text) {
  const rows = [];
  let row = [];
  let cell = "";
  let quoted = false;
  const push = () => { row.push(cell); cell = ""; };
  const commit = () => { if (row.length) { rows.push(row); row = []; } };
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') quoted = false;
      else cell += c;
    } else {
      if (c === '"') quoted = true;
      else if (c === ",") push();
      else if (c === "\n" || c === "\r") {
        if (c === "\r" && text[i + 1] === "\n") i++;
        push(); commit();
      } else cell += c;
    }
  }
  if (cell.length || row.length) { push(); commit(); }
  return rows.filter((r) => r.some((c) => (c || "").trim() !== ""));
}

// -----------------------------------------------------------------------------
// Per-entity configuration. Each tab plugs in canonical fields + endpoint +
// preview table columns + template CSV. Everything else about the wizard is
// shared so all 4 imports feel identical to end users.
// -----------------------------------------------------------------------------
const ENTITY_CONFIGS = {
  residents: {
    key: "residents",
    label: "Residents",
    icon: Home,
    endpoint: "/admin/bulk-import/residents",
    requiresProperty: true,
    supportsWelcomeEmail: true,
    templateFilename: "prestige-residents-template.csv",
    template: `email,name,unit,phone,address,initial_balance
jane.doe@example.com,Jane Doe,4E,+1 (555) 010-2201,"123 Riverside Blvd, Unit 4E",125.00
john.smith@example.com,John Smith,4F,,,`,
    fields: [
      { id: "email", label: "Email", required: true, kind: "string" },
      { id: "name", label: "Full name", required: true, kind: "string" },
      { id: "unit", label: "Unit", required: false, kind: "string" },
      { id: "phone", label: "Phone", required: false, kind: "string" },
      { id: "address", label: "Address", required: false, kind: "string" },
      { id: "initial_balance", label: "Initial balance owed ($)", required: false, kind: "number" },
    ],
    hints: {
      email: ["email", "e-mail"],
      name: ["name", "full name", "resident name"],
      unit: ["unit", "apt", "apartment", "suite", "condo"],
      phone: ["phone", "mobile", "cell", "telephone"],
      address: ["address", "mailing", "street"],
      initial_balance: ["balance", "amount owed", "owed"],
    },
    previewColumns: [
      { key: "email", label: "Email" },
      { key: "name", label: "Name" },
      { key: "unit", label: "Unit" },
    ],
    validate: (row) => row.email && row.name,
  },
  users: {
    key: "users",
    label: "Staff & vendors",
    icon: Users,
    endpoint: "/admin/bulk-import/users",
    requiresProperty: false,
    supportsWelcomeEmail: true,
    templateFilename: "prestige-users-template.csv",
    template: `name,email,role,property_ids,unit,phone
Jane Manager,jane.manager@example.com,admin,,,+15551000001
Ivy Vendor,ivy@acme-hvac.example,vendor,,,+15551000002
Bob Board,bob.board@example.com,board,,,+15551000003`,
    fields: [
      { id: "name", label: "Full name", required: true, kind: "string" },
      { id: "email", label: "Email", required: true, kind: "string" },
      { id: "role", label: "Role (admin/resident/vendor/board/owner/partner)", required: true, kind: "string" },
      { id: "property_ids", label: "Property IDs (semicolon-separated)", required: false, kind: "string" },
      { id: "unit", label: "Unit", required: false, kind: "string" },
      { id: "phone", label: "Phone", required: false, kind: "string" },
    ],
    hints: {
      name: ["name", "full name"],
      email: ["email", "e-mail"],
      role: ["role", "type"],
      property_ids: ["property", "property_id", "property_ids", "properties"],
      unit: ["unit", "apt"],
      phone: ["phone", "mobile", "cell"],
    },
    previewColumns: [
      { key: "email", label: "Email" },
      { key: "name", label: "Name" },
      { key: "role", label: "Role" },
    ],
    validate: (row) => row.email && row.name && row.role,
  },
  vehicles: {
    key: "vehicles",
    label: "Vehicles",
    icon: Car,
    endpoint: "/admin/bulk-import/vehicles",
    requiresProperty: false,
    supportsWelcomeEmail: false,
    templateFilename: "prestige-vehicles-template.csv",
    template: `resident_email,plate,make,model,color,year,state,tag,notes
alice@example.com,ABC1234,Toyota,Camry,silver,2022,FL,resident,Primary vehicle
bob@example.com,BOB5678,Honda,CR-V,black,2021,FL,resident,`,
    fields: [
      { id: "resident_email", label: "Resident email", required: true, kind: "string" },
      { id: "plate", label: "License plate", required: true, kind: "string" },
      { id: "make", label: "Make", required: true, kind: "string" },
      { id: "model", label: "Model", required: true, kind: "string" },
      { id: "color", label: "Color", required: false, kind: "string" },
      { id: "year", label: "Year", required: false, kind: "number" },
      { id: "state", label: "State", required: false, kind: "string" },
      { id: "tag", label: "Tag (resident/visitor/service)", required: false, kind: "string" },
      { id: "notes", label: "Notes", required: false, kind: "string" },
    ],
    hints: {
      resident_email: ["email", "resident_email", "resident email"],
      plate: ["plate", "license", "tag number"],
      make: ["make"],
      model: ["model"],
      color: ["color", "colour"],
      year: ["year", "yr"],
      state: ["state", "province"],
      tag: ["tag", "type", "category"],
      notes: ["notes", "comment", "remark"],
    },
    previewColumns: [
      { key: "resident_email", label: "Resident" },
      { key: "plate", label: "Plate" },
      { key: "make", label: "Make/Model", format: (r) => `${r.make || ""} ${r.model || ""}`.trim() },
    ],
    validate: (row) => row.resident_email && row.plate && row.make && row.model,
  },
  pets: {
    key: "pets",
    label: "Pets",
    icon: PawPrint,
    endpoint: "/admin/bulk-import/pets",
    requiresProperty: false,
    supportsWelcomeEmail: false,
    templateFilename: "prestige-pets-template.csv",
    template: `resident_email,name,species,breed,color,weight_lbs,vax_expiry,notes
alice@example.com,Bella,dog,Golden Retriever,gold,62.5,2027-03-15,Friendly
bob@example.com,Whiskers,cat,Domestic Shorthair,black,10,2027-05-01,Indoor only`,
    fields: [
      { id: "resident_email", label: "Resident email", required: true, kind: "string" },
      { id: "name", label: "Pet name", required: true, kind: "string" },
      { id: "species", label: "Species (dog/cat/…)", required: false, kind: "string" },
      { id: "breed", label: "Breed", required: false, kind: "string" },
      { id: "color", label: "Color", required: false, kind: "string" },
      { id: "weight_lbs", label: "Weight (lbs)", required: false, kind: "number" },
      { id: "vax_expiry", label: "Vaccination expiry (YYYY-MM-DD)", required: false, kind: "string" },
      { id: "notes", label: "Notes", required: false, kind: "string" },
    ],
    hints: {
      resident_email: ["email", "resident_email", "resident email", "owner_email"],
      name: ["pet name", "name"],
      species: ["species", "type"],
      breed: ["breed"],
      color: ["color", "colour"],
      weight_lbs: ["weight", "lbs", "pounds"],
      vax_expiry: ["vax", "vaccine", "shot", "expiry"],
      notes: ["notes", "comment"],
    },
    previewColumns: [
      { key: "resident_email", label: "Resident" },
      { key: "name", label: "Pet" },
      { key: "species", label: "Species" },
    ],
    validate: (row) => row.resident_email && row.name,
  },
  vendors: {
    key: "vendors",
    label: "Vendors",
    icon: HardHat,
    endpoint: "/admin/bulk-import/vendors",
    requiresProperty: false,
    supportsWelcomeEmail: true,
    templateFilename: "prestige-vendors-template.csv",
    template: `name,category,contact,phone,email,property_ids,rating,insurance_expiry,assign_to_pm_categories
Prestigious HVAC Partners,hvac,Service Desk,+15615550110,service@prestigious-hvac.example,,4.8,2027-06-30,hvac
Vertex Elevator Service,elevator,Dispatch,+15615550220,dispatch@vertex-elevator.example,,4.7,2027-04-15,elevator
AquaGuard Fire & Sprinkler,life_safety,Compliance Team,+15615550330,compliance@aquaguard-fire.example,,4.6,2027-05-01,life_safety
ShieldRoof Solutions,roof,Site Manager,+15615550440,sitemgr@shieldroof.example,,4.5,2027-07-20,roof`,
    fields: [
      { id: "name", label: "Vendor name", required: true, kind: "string" },
      { id: "category", label: "Category (hvac/elevator/life_safety/roof/plumbing/electrical/landscaping/general)", required: true, kind: "string" },
      { id: "contact", label: "Contact person", required: false, kind: "string" },
      { id: "phone", label: "Phone", required: false, kind: "string" },
      { id: "email", label: "Email", required: false, kind: "string" },
      { id: "property_ids", label: "Property IDs (semicolon-separated)", required: false, kind: "string" },
      { id: "rating", label: "Rating (0–5)", required: false, kind: "number" },
      { id: "insurance_expiry", label: "Insurance expiry (YYYY-MM-DD)", required: false, kind: "string" },
      { id: "assign_to_pm_categories", label: "Auto-assign to PM categories (semicolon-separated)", required: false, kind: "string" },
    ],
    hints: {
      name: ["name", "vendor", "company"],
      category: ["category", "trade", "service"],
      contact: ["contact", "person", "rep"],
      phone: ["phone", "mobile", "cell"],
      email: ["email", "e-mail"],
      property_ids: ["property", "property_ids", "properties"],
      rating: ["rating", "score", "stars"],
      insurance_expiry: ["insurance", "coi", "expiry"],
      assign_to_pm_categories: ["assign", "pm", "schedule"],
    },
    previewColumns: [
      { key: "name", label: "Vendor" },
      { key: "category", label: "Category" },
      { key: "email", label: "Email" },
    ],
    validate: (row) => row.name && row.category,
  },
  access: {
    key: "access",
    label: "Access credentials",
    icon: KeyRound,
    endpoint: "/admin/bulk-import/access",
    requiresProperty: true,
    supportsWelcomeEmail: false,
    templateFilename: "prestige-access-template.csv",
    template: `holder,unit,type,credential_id,zones,active,notes
Jane Smith,4E,fob,FOB-2201,"Main Gate;Pool;Gym",true,Primary
John Doe,4F,mobile_pass,MP-9942,"Main Gate;Package Room",true,Issued 2026-02
ACME Cleaning,,vendor,VEN-014,"Main Gate;Service Elevator",true,Wednesdays only`,
    fields: [
      { id: "holder", label: "Holder name", required: true, kind: "string" },
      { id: "unit", label: "Unit", required: false, kind: "string" },
      { id: "type", label: "Type (fob/mobile_pass/pin/vendor/key)", required: true, kind: "string" },
      { id: "credential_id", label: "Credential ID", required: true, kind: "string" },
      { id: "zones", label: "Zones (semicolon-separated)", required: false, kind: "string" },
      { id: "active", label: "Active (true/false)", required: false, kind: "string" },
      { id: "notes", label: "Notes", required: false, kind: "string" },
    ],
    hints: {
      holder: ["holder", "name", "resident"],
      unit: ["unit", "apt"],
      type: ["type", "kind"],
      credential_id: ["credential", "id", "fob", "card"],
      zones: ["zones", "areas", "access"],
      active: ["active", "enabled"],
      notes: ["notes", "comment"],
    },
    previewColumns: [
      { key: "holder", label: "Holder" },
      { key: "credential_id", label: "Credential" },
      { key: "type", label: "Type" },
    ],
    validate: (row) => row.holder && row.credential_id && row.type,
  },
  parking: {
    key: "parking",
    label: "Parking",
    icon: ParkingSquare,
    endpoint: "/admin/bulk-import/parking",
    requiresProperty: true,
    supportsWelcomeEmail: false,
    templateFilename: "prestige-parking-template.csv",
    template: `spot,unit,resident_name,plate,vehicle,status
G-101,4E,Jane Smith,ABC-1234,2023 Tesla Model 3,active
G-102,4F,John Doe,XYZ-9876,2021 Honda CR-V,active
G-EV1,2A,Alice Chen,EV-2200,2024 Rivian R1S,ev_charger`,
    fields: [
      { id: "spot", label: "Spot #", required: true, kind: "string" },
      { id: "unit", label: "Unit", required: true, kind: "string" },
      { id: "resident_name", label: "Resident name", required: true, kind: "string" },
      { id: "plate", label: "License plate", required: true, kind: "string" },
      { id: "vehicle", label: "Vehicle", required: true, kind: "string" },
      { id: "status", label: "Status (active/ev_charger/guest/reserved/inactive)", required: false, kind: "string" },
    ],
    hints: {
      spot: ["spot", "space", "stall", "number"],
      unit: ["unit", "apt"],
      resident_name: ["resident", "name", "owner"],
      plate: ["plate", "license", "tag"],
      vehicle: ["vehicle", "car", "make", "model"],
      status: ["status", "type"],
    },
    previewColumns: [
      { key: "spot", label: "Spot" },
      { key: "unit", label: "Unit" },
      { key: "resident_name", label: "Resident" },
    ],
    validate: (row) => row.spot && row.unit && row.resident_name && row.plate && row.vehicle,
  },
  packages: {
    key: "packages",
    label: "Packages",
    icon: Package,
    endpoint: "/admin/bulk-import/packages",
    requiresProperty: true,
    supportsWelcomeEmail: true,     // repurposed as "auto-notify recipient" toggle
    templateFilename: "prestige-packages-template.csv",
    template: `carrier,tracking,unit,resident_name,locker
UPS,1Z999AA10123456784,4E,Jane Smith,L-14
FedEx,7749 8172 4536,4F,John Doe,
USPS,9400 1234 5678 9012,2A,Alice Chen,Shelf 3B`,
    fields: [
      { id: "carrier", label: "Carrier", required: true, kind: "string" },
      { id: "tracking", label: "Tracking #", required: true, kind: "string" },
      { id: "unit", label: "Unit", required: true, kind: "string" },
      { id: "resident_name", label: "Resident name", required: true, kind: "string" },
      { id: "locker", label: "Locker / shelf", required: false, kind: "string" },
    ],
    hints: {
      carrier: ["carrier", "courier", "shipper"],
      tracking: ["tracking", "track", "number"],
      unit: ["unit", "apt"],
      resident_name: ["resident", "name", "recipient"],
      locker: ["locker", "shelf", "location"],
    },
    previewColumns: [
      { key: "carrier", label: "Carrier" },
      { key: "tracking", label: "Tracking" },
      { key: "unit", label: "Unit" },
    ],
    validate: (row) => row.carrier && row.tracking && row.unit && row.resident_name,
  },
};

const ENTITY_ORDER = ["residents", "users", "vehicles", "pets", "vendors", "access", "parking", "packages"];

// -----------------------------------------------------------------------------
export default function BulkImportPage() {
  const { propertyId, currentProperty } = useProperty();
  const [entity, setEntity] = useState("residents");
  const config = ENTITY_CONFIGS[entity];

  const [step, setStep] = useState(1); // 1=paste 2=map 3=preview 4=done
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState({ headers: [], rows: [] });
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendWelcome, setSendWelcome] = useState(false);
  const [defaultPassword, setDefaultPassword] = useState("Welcome@2026");
  const [commitResult, setCommitResult] = useState(null);
  const [rowStatuses, setRowStatuses] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  const canProceedMap = useMemo(() => {
    return config.fields.filter((f) => f.required).every((f) => mapping[f.id] != null);
  }, [mapping, config.fields]);

  const resetForEntity = (newEntity) => {
    setEntity(newEntity);
    setStep(1); setText(""); setParsed({ headers: [], rows: [] });
    setMapping({}); setPreview(null); setCommitResult(null); setRowStatuses([]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const parseAndMove = () => {
    const rows = parseCSV(text);
    if (rows.length < 2) return toast.error("CSV needs at least a header row + 1 data row.");
    const [headers, ...rest] = rows;
    setParsed({ headers, rows: rest });
    // Auto-map columns by fuzzy header match
    const nextMapping = {};
    headers.forEach((h, idx) => {
      const norm = (h || "").trim().toLowerCase();
      for (const f of config.fields) {
        if (nextMapping[f.id] != null) continue;
        const hints = config.hints[f.id] || [f.id];
        if (hints.some((hint) => norm === hint || norm.includes(hint))) {
          nextMapping[f.id] = idx;
        }
      }
    });
    setMapping(nextMapping);
    setStep(2);
  };

  const onFile = async (file) => {
    if (!file) return;
    const buf = await file.text();
    setText(buf);
    toast.success(`Loaded ${file.name} (${(file.size / 1024).toFixed(1)} KB)`);
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv") && !file.type.includes("csv")) {
      return toast.error("Please drop a .csv file.");
    }
    await onFile(file);
  };

  const buildRows = () =>
    parsed.rows.map((r) => {
      const out = {};
      config.fields.forEach((f) => {
        const idx = mapping[f.id];
        if (idx == null) return;
        const v = (r[idx] || "").trim();
        if (!v) return;
        if (f.kind === "number") {
          const n = parseFloat(v.replace(/[$,]/g, ""));
          if (!Number.isNaN(n)) out[f.id] = n;
        } else {
          out[f.id] = v;
        }
      });
      return out;
    }).filter(config.validate);

  const runPreview = async () => {
    if (config.requiresProperty && !propertyId) {
      return toast.error("Pick a property first (use the property switcher in the header).");
    }
    setBusy(true);
    try {
      const rows = buildRows();
      if (!rows.length) {
        toast.error("No valid rows after applying your column mapping. Double-check the required columns.");
        setBusy(false);
        return;
      }
      const body = { rows, dry_run: true };
      if (config.requiresProperty) body.property_id = propertyId;
      else if (propertyId) body.default_property_id = propertyId;
      const { data } = await api.post(config.endpoint, body);
      setPreview(data);
      // Initialise per-row statuses to 'ready' for the commit step
      setRowStatuses(rows.map((r) => ({ row: r, status: "ready", message: "" })));
      setStep(3);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const rows = buildRows();
      const body = { rows, dry_run: false };
      if (config.requiresProperty) body.property_id = propertyId;
      else if (propertyId) body.default_property_id = propertyId;
      if (config.supportsWelcomeEmail) {
        if (config.key === "residents") {
          body.default_password = defaultPassword;
          body.send_welcome = sendWelcome;
        } else if (config.key === "users") {
          body.send_invites = sendWelcome;
        }
      }
      if (config.key === "vendors") body.send_onboarding_invites = sendWelcome;
      if (config.key === "packages") body.notify = sendWelcome;
      const { data } = await api.post(config.endpoint, body);
      setCommitResult(data);
      // Mark every submitted row as committed (backend returns aggregate counts,
      // so we surface the summary and let admins retry any row individually).
      setRowStatuses((cur) => cur.map((r) => ({ ...r, status: "ready" })));
      setStep(4);
      const created = data.created ?? 0;
      toast.success(`Created ${created} ${config.label.toLowerCase()}${created === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Commit failed");
    } finally {
      setBusy(false);
    }
  };

  const retryRow = async (idx) => {
    const target = rowStatuses[idx];
    if (!target) return;
    setRowStatuses((cur) => cur.map((r, i) => i === idx ? { ...r, status: "retrying", message: "" } : r));
    try {
      const body = { rows: [target.row], dry_run: false };
      if (config.requiresProperty) body.property_id = propertyId;
      else if (propertyId) body.default_property_id = propertyId;
      if (config.supportsWelcomeEmail) {
        if (config.key === "residents") {
          body.default_password = defaultPassword;
          body.send_welcome = sendWelcome;
        } else if (config.key === "users") {
          body.send_invites = sendWelcome;
        }
      }
      if (config.key === "vendors") body.send_onboarding_invites = sendWelcome;
      if (config.key === "packages") body.notify = sendWelcome;
      const { data } = await api.post(config.endpoint, body);
      const ok = (data.created ?? 0) > 0;
      setRowStatuses((cur) => cur.map((r, i) => i === idx ? {
        ...r,
        status: ok ? "created" : (data.skipped > 0 ? "skipped" : "failed"),
        message: ok ? "Created" : (data.skipped > 0 ? "Already existed" : (data.errors?.[0] || "Failed")),
      } : r));
      if (ok) toast.success("Retry succeeded — row created");
    } catch (e) {
      const msg = apiErrorMessage(e) || "Retry failed";
      setRowStatuses((cur) => cur.map((r, i) => i === idx ? { ...r, status: "failed", message: msg } : r));
      toast.error(msg);
    }
  };

  const reset = () => {
    setStep(1); setText(""); setParsed({ headers: [], rows: [] });
    setMapping({}); setPreview(null); setCommitResult(null); setRowStatuses([]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const downloadTemplate = () => {
    const blob = new Blob([config.template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = config.templateFilename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const previewRows = preview?.sample_preview || [];

  return (
    <div>
      <PageHeader
        eyebrow="Admin Tools"
        title="Bulk Import"
        subtitle={currentProperty
          ? `Onboarding into ${currentProperty.name} — drag a CSV in or paste one below.`
          : "Onboard residents, staff, vehicles, and pets from a single spreadsheet."}
      />

      {/* Entity tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="import-entity-tabs">
        {ENTITY_ORDER.map((key) => {
          const c = ENTITY_CONFIGS[key];
          const Icon = c.icon;
          const active = entity === key;
          return (
            <button
              key={key}
              data-testid={`import-tab-${key}`}
              onClick={() => resetForEntity(key)}
              className={
                "flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition " +
                (active
                  ? "bg-orange-500 text-white shadow shadow-orange-500/30"
                  : "bg-white/[0.03] text-zinc-300 border border-white/10 hover:bg-white/[0.06]")
              }
            >
              <Icon className="h-3.5 w-3.5" /> {c.label}
            </button>
          );
        })}
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-4">
        {[
          { n: 1, label: "Upload CSV" },
          { n: 2, label: "Map columns" },
          { n: 3, label: "Preview" },
          { n: 4, label: "Done" },
        ].map((s, i, arr) => (
          <div key={s.n} className="flex items-center gap-2">
            <div
              data-testid={`import-step-${s.n}`}
              className={
                "h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold " +
                (step === s.n ? "bg-orange-500 text-white"
                  : step > s.n ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                    : "bg-white/[0.04] text-zinc-500 border border-white/10")
              }
            >{step > s.n ? "✓" : s.n}</div>
            <div className={"text-xs " + (step === s.n ? "text-white" : "text-zinc-500")}>{s.label}</div>
            {i < arr.length - 1 && <div className="h-px w-8 bg-white/10 mx-1" />}
          </div>
        ))}
      </div>

      {/* STEP 1 — upload */}
      {step === 1 && (
        <Card className="border-white/10 bg-card p-5" data-testid="import-step1-card">
          <div className="flex items-start justify-between mb-3 gap-3">
            <div>
              <div className="overline">Upload</div>
              <div className="text-lg font-semibold font-display mt-1">Drop a CSV, paste one, or use a template</div>
              <div className="text-sm text-zinc-400 mt-1">Include a header row. Any header names work — you&apos;ll map them in the next step.</div>
            </div>
            <Button variant="outline" onClick={downloadTemplate} className="border-white/10 shrink-0" data-testid="import-download-template">
              <Download className="h-3.5 w-3.5 mr-1" /> {config.label} template
            </Button>
          </div>

          <div
            data-testid="import-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={
              "rounded-xl border-2 border-dashed p-6 mb-3 text-center transition " +
              (dragOver
                ? "border-orange-500 bg-orange-500/10"
                : "border-white/15 bg-white/[0.02] hover:border-white/25")
            }
          >
            <UploadCloud className={"h-8 w-8 mx-auto mb-2 " + (dragOver ? "text-orange-300" : "text-zinc-500")} />
            <div className="text-sm text-zinc-300">
              {dragOver ? "Release to load" : "Drag & drop your .csv here"}
            </div>
            <div className="text-xs text-zinc-500 mt-1">— or —</div>
            <input
              ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])} data-testid="import-file-input"
            />
            <Button
              variant="outline" onClick={() => fileRef.current?.click()}
              className="border-white/10 mt-2" data-testid="import-file-pick"
            >
              <FileText className="h-3.5 w-3.5 mr-1" /> Choose file
            </Button>
          </div>

          <Textarea
            data-testid="bulk-csv"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={8}
            placeholder={config.template.split("\n").slice(0, 1).join("\n") + "..."}
            className="font-mono text-xs bg-white/[0.04] border-white/10 text-white"
          />
          <div className="mt-4 flex justify-end">
            <Button onClick={parseAndMove} disabled={!text.trim()} className="bg-orange-500 hover:bg-orange-600 rounded-full" data-testid="import-parse">
              Next: map columns <ArrowRight className="h-3.5 w-3.5 ml-1" />
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 2 — map */}
      {step === 2 && (
        <Card className="border-white/10 bg-card p-5" data-testid="import-step2-card">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="overline">Column mapping</div>
              <div className="text-lg font-semibold font-display mt-1">Match your CSV columns to Prestigious fields</div>
              <div className="text-sm text-zinc-400 mt-1">We auto-guessed based on your headers. Adjust anything that looks off.</div>
            </div>
            <Badge variant="outline" className="border-white/10 text-zinc-300">{parsed.rows.length} rows</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {config.fields.map((f) => (
              <div key={f.id} className="rounded-md border border-white/10 bg-white/[0.02] p-3">
                <Label className="text-xs uppercase tracking-widest text-zinc-400 flex items-center gap-2">
                  {f.label}
                  {f.required && <span className="text-orange-400 text-[10px]">REQUIRED</span>}
                </Label>
                <select
                  data-testid={`import-map-${f.id}`}
                  className="w-full mt-1 bg-white/[0.04] border border-white/10 text-white rounded-md px-2 py-1.5 text-sm"
                  value={mapping[f.id] ?? ""}
                  onChange={(e) => setMapping({ ...mapping, [f.id]: e.target.value === "" ? undefined : Number(e.target.value) })}
                >
                  <option value="">— skip —</option>
                  {parsed.headers.map((h, idx) => (
                    <option key={idx} value={idx}>{h || `(column ${idx + 1})`}</option>
                  ))}
                </select>
                {mapping[f.id] != null && parsed.rows[0]?.[mapping[f.id]] != null && (
                  <div className="text-[11px] text-zinc-500 mt-1 truncate">
                    e.g. {parsed.rows[0][mapping[f.id]]}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-5 flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)} className="border-white/10" data-testid="import-back-to-1">
              <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
            </Button>
            <Button
              disabled={!canProceedMap || busy}
              onClick={runPreview}
              className="bg-orange-500 hover:bg-orange-600 rounded-full"
              data-testid="import-run-preview"
            >
              {busy ? "Checking…" : <>Preview <ArrowRight className="h-3.5 w-3.5 ml-1" /></>}
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 3 — preview */}
      {step === 3 && preview && (
        <Card className="border-white/10 bg-card p-5" data-testid="import-step3-card">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatBox tone="neutral" label="Total rows" value={preview.total} />
            <StatBox tone="ok"    label="Will create" value={preview.will_create} testid="import-preview-create" />
            <StatBox tone="warn"  label="Will skip"   value={preview.will_skip}   testid="import-preview-skip" />
            <StatBox tone={preview.errors?.length ? "err" : "neutral"}
                     label="Validation errors"
                     value={preview.errors?.length || 0}
                     testid="import-preview-errors" />
          </div>

          {preview.errors?.length > 0 && (
            <div className="rounded-md border border-red-500/20 bg-red-500/[0.04] p-3 mb-4">
              <div className="text-sm font-semibold text-red-200 flex items-center gap-2 mb-1"><AlertTriangle className="h-4 w-4" /> Fix these first</div>
              <ul className="text-xs text-red-200 space-y-0.5">
                {preview.errors.slice(0, 20).map((e) => <li key={e}>• {e}</li>)}
              </ul>
            </div>
          )}

          <div className="rounded-md border border-white/10 overflow-hidden mb-4">
            <table className="w-full text-xs">
              <thead className="bg-white/[0.03]">
                <tr>
                  {config.previewColumns.map((c) => (
                    <th key={c.key} className="text-left px-3 py-2 text-zinc-400">{c.label}</th>
                  ))}
                  <th className="text-right px-3 py-2 text-zinc-400">Status</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((r) => (
                  <tr key={r.id || r.email || r.name || r.tracking || r.plate || `${r.spot || ""}-${r.holder || r.resident_name || ""}`} className="border-t border-white/5">
                    {config.previewColumns.map((c) => (
                      <td key={c.key} className="px-3 py-1.5 text-zinc-300">
                        {c.format ? c.format(r) : (r[c.key] || "—")}
                      </td>
                    ))}
                    <td className="px-3 py-1.5 text-right">
                      <Badge variant="outline" className={
                        (r.status || "").includes("skip")
                          ? "border-amber-500/40 text-amber-300"
                          : "border-emerald-500/40 text-emerald-300"
                      }>
                        {r.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.total > previewRows.length && (
              <div className="px-3 py-2 text-[11px] text-zinc-500 border-t border-white/5">
                Showing first {previewRows.length} of {preview.total} rows.
              </div>
            )}
          </div>

          {config.supportsWelcomeEmail && (
            <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 mb-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-white">
                    {config.key === "residents" ? "Send welcome email" : "Send invite email"}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {config.key === "residents"
                      ? "Emails each new resident a \u201cset your password\u201d link (14-day expiry). If off, everyone gets the default password below."
                      : config.key === "vendors"
                        ? "Fires the 4-step vendor onboarding invite (confirm info → set password → upload W-9 → upload COI) to every new vendor."
                        : config.key === "packages"
                          ? "Fire the arrival notification (push + email + SMS) to the resident for every imported row. Off by default for backfills — turn on if you're catching up on today's real deliveries."
                          : "Fires a set-password invite so each staff member picks their own credentials on first login."}
                  </div>
                </div>
                <Switch checked={sendWelcome} onCheckedChange={setSendWelcome} data-testid="import-send-welcome" />
              </div>
              {config.key === "residents" && !sendWelcome && (
                <div>
                  <Label className="text-xs uppercase tracking-widest text-zinc-400">Default password (they&apos;ll be prompted to change on first login)</Label>
                  <Input
                    data-testid="import-default-password"
                    value={defaultPassword}
                    onChange={(e) => setDefaultPassword(e.target.value)}
                    className="mt-1 bg-white/[0.04] border-white/10 text-white"
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)} className="border-white/10" data-testid="import-back-to-2">
              <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
            </Button>
            <Button
              disabled={busy || preview.will_create === 0 || preview.errors?.length > 0}
              onClick={commit}
              className="bg-orange-500 hover:bg-orange-600 rounded-full"
              data-testid="import-commit"
            >
              {busy ? "Importing…" : (
                <>Import {preview.will_create} {config.label.toLowerCase()} <UploadCloud className="h-4 w-4 ml-1" /></>
              )}
            </Button>
          </div>
        </Card>
      )}

      {/* STEP 4 — done */}
      {step === 4 && commitResult && (
        <Card className="border-white/10 bg-card p-5" data-testid="import-step4-card">
          <div className="text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-3" />
            <div className="text-2xl font-semibold font-display text-white">
              Imported {commitResult.created} {config.label.toLowerCase()}
            </div>
            <div className="text-sm text-zinc-400 mt-1">
              Skipped {commitResult.skipped || 0} (already existed or duplicates).
              {config.key === "residents" && (
                commitResult.welcome_emails_sent
                  ? " Welcome emails on the way — each resident sets their own password."
                  : <> Default login password: <span className="font-mono text-white">{commitResult.default_password}</span></>
              )}
            </div>
          </div>

          {/* Per-row retry table — admins can hammer any single row until it lands */}
          {rowStatuses.length > 0 && (
            <div className="mt-6 rounded-md border border-white/10 overflow-hidden">
              <div className="px-3 py-2 bg-white/[0.03] text-xs uppercase tracking-widest text-zinc-400">
                Per-row status ({rowStatuses.length} rows)
              </div>
              <table className="w-full text-xs">
                <thead className="bg-white/[0.02]">
                  <tr>
                    {config.previewColumns.map((c) => (
                      <th key={c.key} className="text-left px-3 py-1.5 text-zinc-500">{c.label}</th>
                    ))}
                    <th className="text-left px-3 py-1.5 text-zinc-500">Status</th>
                    <th className="text-right px-3 py-1.5 text-zinc-500">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rowStatuses.map((rs, i) => {
                    const rowKey = rs.row.email || rs.row.plate || rs.row.tracking
                                    || `${rs.row.spot || ""}-${rs.row.holder || rs.row.resident_name || i}`
                                    || `import-row-${i}`;
                    return (
                      <tr key={rowKey} className="border-t border-white/5" data-testid={`row-status-${i}`}>
                        {config.previewColumns.map((c) => (
                          <td key={c.key} className="px-3 py-1.5 text-zinc-300">
                            {c.format ? c.format(rs.row) : (rs.row[c.key] || "—")}
                          </td>
                        ))}
                        <td className="px-3 py-1.5">
                          <RowStatusPill status={rs.status} message={rs.message} />
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          {(rs.status === "failed" || rs.status === "ready" || rs.status === "skipped") && (
                            <Button
                              size="sm" variant="outline"
                              onClick={() => retryRow(i)}
                              className="border-white/10 h-7 px-2 text-[11px]"
                              data-testid={`retry-row-${i}`}
                            >
                              <RefreshCw className="h-3 w-3 mr-1" /> Retry
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-6 flex items-center justify-center gap-3">
            <Button variant="outline" onClick={reset} className="border-white/10" data-testid="import-restart">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Import another batch
            </Button>
            <Button asChild className="bg-orange-500 hover:bg-orange-600 rounded-full">
              <a href={config.key === "users" ? "/admin/users"
                        : config.key === "access" ? "/admin/access"
                        : config.key === "parking" ? "/admin/parking"
                        : `/admin/${config.key}`}>
                See {config.label.toLowerCase()}
              </a>
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
function StatBox({ label, value, tone = "neutral", testid }) {
  const toneMap = {
    neutral: "border-white/10",
    ok: "border-emerald-500/20 bg-emerald-500/[0.04]",
    warn: "border-amber-500/20 bg-amber-500/[0.04]",
    err: "border-red-500/20 bg-red-500/[0.04]",
  };
  const labelTone = {
    neutral: "text-zinc-500",
    ok: "text-emerald-300",
    warn: "text-amber-300",
    err: "text-red-300",
  };
  return (
    <div className={"rounded-md border p-3 " + toneMap[tone]} data-testid={testid}>
      <div className={"text-[10px] uppercase tracking-widest " + labelTone[tone]}>{label}</div>
      <div className="text-2xl font-semibold text-white mt-1">{value}</div>
    </div>
  );
}

function RowStatusPill({ status, message }) {
  const map = {
    ready:    { color: "border-white/15 text-zinc-300", label: "Committed" },
    created:  { color: "border-emerald-500/40 text-emerald-300", label: "Created" },
    skipped:  { color: "border-amber-500/40 text-amber-300", label: message || "Skipped" },
    failed:   { color: "border-red-500/40 text-red-300", label: message || "Failed" },
    retrying: { color: "border-orange-500/40 text-orange-300", label: "Retrying…" },
  };
  const { color, label } = map[status] || map.ready;
  return (
    <div className="flex items-center gap-2">
      <Badge variant="outline" className={color}>{label}</Badge>
      {status === "failed" && message && (
        <span className="text-[11px] text-red-300 truncate max-w-[220px]" title={message}>{message}</span>
      )}
    </div>
  );
}
